<template>
  <v-card class="save-template-modal">
    <v-card-text class="save-template-modal__body">
      <div class="save-template-modal__hint">
        Give this template a name so you can reuse the same survey structure in the future.
      </div>

      <AppTextField
        v-model="templateName"
        label="Template name"
        placeholder="e.g. Standard inspection survey"
      />

      <div v-if="errorMessage" class="save-template-modal__error">
        {{ errorMessage }}
      </div>
    </v-card-text>

    <v-card-actions class="save-template-modal__actions">
      <AppButtonFlat
        color="primary"
        block
        prepend-icon="mdi-content-save-outline"
        :loading="isSaving"
        :disabled="isSaving || !templateName.trim()"
        @click="save"
      >
        Save template
      </AppButtonFlat>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useModalStore } from "@/shared/stores/modal";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import { saveSurveyTemplate } from "@/shared/services/surveys.api";

const modal = useModalStore();

const props = withDefaults(
  defineProps<{
    surveyId?: number | null;
    onSave?: (payload: { template_name: string; survey_id?: number | null }) => void | Promise<void>;
  }>(),
  {
    surveyId: null,
    onSave: undefined,
  },
);

const templateName = ref("");
const isSaving = ref(false);
const errorMessage = ref("");

async function save() {
  if (isSaving.value) return;
  isSaving.value = true;
  errorMessage.value = "";
  try {
    const payload = {
      template_name: templateName.value.trim(),
      survey_id: props.surveyId ?? null,
    };
    await saveSurveyTemplate(payload);
    await props.onSave?.(payload);
    modal.close();
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      (typeof error === "string" ? error : null);
    errorMessage.value = message ? String(message) : "Something went wrong. Please try again.";
  } finally {
    isSaving.value = false;
  }
}
</script>

<style scoped>
.save-template-modal {
  max-width: 520px;
}

.save-template-modal__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.save-template-modal__hint {
  font-size: 14px;
  color: var(--color-subtitle, #475569);
}

.save-template-modal__actions {
  padding: 10px 14px;
}

.save-template-modal__error {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(220, 38, 38, 0.25);
  background: rgba(220, 38, 38, 0.06);
  color: #b91c1c;
  font-size: 14px;
  font-weight: 600;
  white-space: pre-wrap;
}
</style>
